import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import './MermaidDiagram.css';

function MermaidDiagram({ chart, title }) {
  const mermaidRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [fullSizeSvg, setFullSizeSvg] = useState('');
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    const renderDiagram = async () => {
      if (chart && mermaidRef.current) {
        try {
          // Detectar tema actual
          const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';

          // Configurar Mermaid con el tema correcto
          mermaid.initialize({
            startOnLoad: false,
            theme: currentTheme === 'dark' ? 'dark' : 'neutral',
            securityLevel: 'loose',
            fontFamily: 'Arial, sans-serif',
            flowchart: {
              useMaxWidth: true,
              htmlLabels: true,
              curve: 'basis'
            },
            themeVariables: currentTheme === 'dark' ? {
              darkMode: true,
              background: '#16213e',
              primaryColor: '#667eea',
              primaryTextColor: '#e0e0e0',
              primaryBorderColor: '#667eea',
              lineColor: '#667eea',
              secondaryColor: '#0f3460',
              tertiaryColor: '#1a1a2e',
              mainBkg: '#16213e',
              secondBkg: '#0f3460',
              mainContrastColor: '#e0e0e0',
              darkTextColor: '#e0e0e0',
              textColor: '#e0e0e0',
              labelTextColor: '#e0e0e0',
              fontSize: '16px',
              edgeLabelBackground: '#16213e'
            } : {
              edgeLabelBackground: '#ffffff'
            }
          });

          // Generar ID único para el diagrama
          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

          // Renderizar el diagrama
          const { svg: renderedSvg } = await mermaid.render(id, chart);

          // Modificar el SVG para cambiar el fondo de las etiquetas
          let modifiedSvg = renderedSvg;

          // Reemplazar background-color: white con el color del tema
          const bgColor = currentTheme === 'dark' ? '#16213e' : '#ffffff';
          modifiedSvg = modifiedSvg.replace(/background-color:\s*white/g, `background-color: ${bgColor}`);
          modifiedSvg = modifiedSvg.replace(/background-color:\s*#fff/g, `background-color: ${bgColor}`);

          setSvg(modifiedSvg);

          // Renderizar versión full-size para el zoom (sin useMaxWidth)
          mermaid.initialize({
            startOnLoad: false,
            theme: currentTheme === 'dark' ? 'dark' : 'neutral',
            securityLevel: 'loose',
            fontFamily: 'Arial, sans-serif',
            flowchart: {
              useMaxWidth: false,
              htmlLabels: true,
              curve: 'basis'
            },
            themeVariables: currentTheme === 'dark' ? {
              darkMode: true,
              background: '#16213e',
              primaryColor: '#667eea',
              primaryTextColor: '#e0e0e0',
              primaryBorderColor: '#667eea',
              lineColor: '#667eea',
              secondaryColor: '#0f3460',
              tertiaryColor: '#1a1a2e',
              mainBkg: '#16213e',
              secondBkg: '#0f3460',
              mainContrastColor: '#e0e0e0',
              darkTextColor: '#e0e0e0',
              textColor: '#e0e0e0',
              labelTextColor: '#e0e0e0',
              fontSize: '16px',
              edgeLabelBackground: '#16213e'
            } : {
              edgeLabelBackground: '#ffffff'
            }
          });

          const idFull = `mermaid-full-${Math.random().toString(36).substr(2, 9)}`;
          const { svg: renderedFullSvg } = await mermaid.render(idFull, chart);

          let modifiedFullSvg = renderedFullSvg;
          modifiedFullSvg = modifiedFullSvg.replace(/background-color:\s*white/g, `background-color: ${bgColor}`);
          modifiedFullSvg = modifiedFullSvg.replace(/background-color:\s*#fff/g, `background-color: ${bgColor}`);

          setFullSizeSvg(modifiedFullSvg);
        } catch (error) {
          console.error('Error rendering mermaid diagram:', error);
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = `<div style="color: red; padding: 20px;">Error al renderizar el diagrama</div>`;
          }
        }
      }
    };

    renderDiagram();
  }, [chart]);

  // Re-renderizar cuando cambie el tema
  useEffect(() => {
    const handleThemeChange = () => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            const renderDiagram = async () => {
              if (chart && mermaidRef.current) {
                try {
                  const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';

                  mermaid.initialize({
                    startOnLoad: false,
                    theme: currentTheme === 'dark' ? 'dark' : 'neutral',
                    securityLevel: 'loose',
                    fontFamily: 'Arial, sans-serif',
                    flowchart: {
                      useMaxWidth: true,
                      htmlLabels: true,
                      curve: 'basis'
                    },
                    themeVariables: currentTheme === 'dark' ? {
                      darkMode: true,
                      background: '#16213e',
                      primaryColor: '#667eea',
                      primaryTextColor: '#e0e0e0',
                      primaryBorderColor: '#667eea',
                      lineColor: '#667eea',
                      secondaryColor: '#0f3460',
                      tertiaryColor: '#1a1a2e',
                      mainBkg: '#16213e',
                      secondBkg: '#0f3460',
                      mainContrastColor: '#e0e0e0',
                      darkTextColor: '#e0e0e0',
                      textColor: '#e0e0e0',
                      labelTextColor: '#e0e0e0',
                      fontSize: '16px',
                      edgeLabelBackground: '#16213e'
                    } : {
                      edgeLabelBackground: '#ffffff'
                    }
                  });

                  const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
                  const { svg: renderedSvg } = await mermaid.render(id, chart);

                  let modifiedSvg = renderedSvg;
                  const bgColor = currentTheme === 'dark' ? '#16213e' : '#ffffff';
                  modifiedSvg = modifiedSvg.replace(/background-color:\s*white/g, `background-color: ${bgColor}`);
                  modifiedSvg = modifiedSvg.replace(/background-color:\s*#fff/g, `background-color: ${bgColor}`);

                  setSvg(modifiedSvg);

                  // Renderizar versión full-size para el zoom
                  mermaid.initialize({
                    startOnLoad: false,
                    theme: currentTheme === 'dark' ? 'dark' : 'neutral',
                    securityLevel: 'loose',
                    fontFamily: 'Arial, sans-serif',
                    flowchart: {
                      useMaxWidth: false,
                      htmlLabels: true,
                      curve: 'basis'
                    },
                    themeVariables: currentTheme === 'dark' ? {
                      darkMode: true,
                      background: '#16213e',
                      primaryColor: '#667eea',
                      primaryTextColor: '#e0e0e0',
                      primaryBorderColor: '#667eea',
                      lineColor: '#667eea',
                      secondaryColor: '#0f3460',
                      tertiaryColor: '#1a1a2e',
                      mainBkg: '#16213e',
                      secondBkg: '#0f3460',
                      mainContrastColor: '#e0e0e0',
                      darkTextColor: '#e0e0e0',
                      textColor: '#e0e0e0',
                      labelTextColor: '#e0e0e0',
                      fontSize: '16px',
                      edgeLabelBackground: '#16213e'
                    } : {
                      edgeLabelBackground: '#ffffff'
                    }
                  });

                  const idFull = `mermaid-full-${Math.random().toString(36).substr(2, 9)}`;
                  const { svg: renderedFullSvg } = await mermaid.render(idFull, chart);

                  let modifiedFullSvg = renderedFullSvg;
                  modifiedFullSvg = modifiedFullSvg.replace(/background-color:\s*white/g, `background-color: ${bgColor}`);
                  modifiedFullSvg = modifiedFullSvg.replace(/background-color:\s*#fff/g, `background-color: ${bgColor}`);

                  setFullSizeSvg(modifiedFullSvg);
                } catch (error) {
                  console.error('Error rendering mermaid diagram:', error);
                }
              }
            };
            renderDiagram();
          }
        });
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });

      return () => observer.disconnect();
    };

    return handleThemeChange();
  }, [chart]);

  // Cerrar zoom con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isZoomed) {
        setIsZoomed(false);
      }
    };

    if (isZoomed) {
      document.addEventListener('keydown', handleEscape);
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomed]);

  const handleDiagramClick = () => {
    setIsZoomed(true);
  };

  const handleCloseZoom = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsZoomed(false);
  };

  const handleOverlayClick = (e) => {
    // No cerrar al hacer clic en el overlay, solo con la X o ESC
    return;
  };

  return (
    <>
      <div className="mermaid-container">
        {title && <h3 className="diagram-subtitle">{title}</h3>}
        <div className="mermaid-wrapper">
          <div
            ref={mermaidRef}
            className="mermaid-diagram"
            onClick={handleDiagramClick}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
      </div>

      {/* Modal de zoom con zoom/pan interactivo */}
      {isZoomed && (
        <div className="diagram-zoom-overlay active">
          <button
            className="diagram-zoom-close"
            onClick={handleCloseZoom}
            aria-label="Cerrar zoom"
          >
            ×
          </button>
          <div className="diagram-zoom-instructions">
            <span>🔍 Rueda del ratón para zoom</span>
            <span>👆 Arrastra para mover</span>
            <span>❌ ESC o X para cerrar</span>
          </div>
          <TransformWrapper
            initialScale={0.8}
            minScale={0.3}
            maxScale={4}
            centerOnInit={true}
            limitToBounds={false}
            wheel={{ step: 0.1 }}
            doubleClick={{ mode: 'zoomIn' }}
            panning={{
              disabled: false,
              velocityDisabled: false,
              lockAxisX: false,
              lockAxisY: false
            }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <div className="diagram-zoom-controls">
                  <button onClick={() => zoomIn()} title="Acercar">+</button>
                  <button onClick={() => zoomOut()} title="Alejar">−</button>
                  <button onClick={() => resetTransform()} title="Reiniciar">⟲</button>
                </div>
                <TransformComponent>
                  <div dangerouslySetInnerHTML={{ __html: fullSizeSvg || svg }} />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
      )}
    </>
  );
}

export default MermaidDiagram;
